import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        // Simulate form submission
        setTimeout(() => {
            // Store in localStorage as mock backend
            const existingMessages = JSON.parse(localStorage.getItem('focusBloomMessages') || '[]');
            existingMessages.push({
                ...formData,
                timestamp: new Date().toISOString()
            });
            localStorage.setItem('focusBloomMessages', JSON.stringify(existingMessages));
            
            toast.success('Thank you for your message!', {
                description: 'We\'ll get back to you soon.'
            });
            
            setFormData({ name: '', email: '', message: '' });
            setIsSubmitting(false);
        }, 1000);
    };
    
    return (
        <div className="min-h-screen pt-32 pb-20 px-6 sm:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-16 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-full bg-primary/10 animate-breathe">
                        <Mail className="w-10 h-10 text-primary" />
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light text-foreground mb-6">
                        Get in Touch
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        We'd love to hear from you. Share your feedback, ask questions, or simply say hello.
                    </p>
                </div>
                
                {/* Contact Form */}
                <Card className="p-8 sm:p-12 bg-card border-border animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name Field */}
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-foreground">
                                Name
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your name"
                                required
                                className="rounded-lg"
                            />
                        </div>
                        
                        {/* Email Field */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-foreground">
                                Email
                            </Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="your.email@example.com"
                                required
                                className="rounded-lg"
                            />
                        </div>
                        
                        {/* Message Field */}
                        <div className="space-y-2">
                            <Label htmlFor="message" className="text-foreground">
                                Message
                            </Label>
                            <Textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Share your thoughts, feedback, or questions..."
                                required
                                rows={6}
                                className="rounded-lg resize-none"
                            />
                        </div>
                        
                        {/* Submit Button */}
                        <Button
                            type="submit"
                            size="lg"
                            disabled={isSubmitting}
                            className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 hover:shadow-lg"
                        >
                            {isSubmitting ? (
                                'Sending...'
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Message
                                </>
                            )}
                        </Button>
                    </form>
                </Card>
                
                {/* Additional Info */}
                <Card className="mt-8 p-6 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-border text-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
                    <p className="text-muted-foreground">
                        Your privacy matters to us. We'll never share your information with third parties.
                    </p>
                </Card>
            </div>
        </div>
    );
};